import api from './api';

/**
 * سرویس «دستیار گروه‌ها» — آینهٔ `figano-backend/src/routes/admin/userbot.routes.ts`
 * (apiService پاسخ `{success, data}` را باز می‌کند و `data` را برمی‌گرداند)
 */

export type UserbotAccountStatus = 'new' | 'code_sent' | 'password_needed' | 'active' | 'disabled' | 'error';

export interface UserbotPersona {
  displayName: string;
  style: string;
  bio: string;
  extraRules: string;
}

export interface UserbotAccount {
  _id: string;
  label: string;
  phone: string;
  status: UserbotAccountStatus;
  tgUserId?: string;
  username?: string;
  firstName?: string;
  lastName?: string;
  persona: UserbotPersona;
  limits: { dailyReplyCap: number; minGapSeconds: number; maxPerGroupPerHour: number };
  active: boolean;
  online: boolean;
  lastSeenAt?: string;
  lastError?: string;
  lastErrorAt?: string;
  floodWaitUntil?: string;
  stats: { repliesTotal: number; repliesToday: number; repliesDay: string; lastReplyAt?: string };
  login?: { codeSentAt?: string; isCodeViaApp?: boolean };
  groupsCount?: number;
  createdAt: string;
}

export interface UserbotGroup {
  _id: string;
  chatId: string;
  title: string;
  username?: string;
  membersCount?: number;
  enabled: boolean;
  mode: 'draft' | 'auto';
  accountIds: Array<{ _id: string; label: string; username?: string; online?: boolean; status?: string; active?: boolean }>;
  preferredAccountId?: { _id: string; label: string } | null;
  sensitivity: 'low' | 'normal' | 'high';
  cooldown: { perUserMinutes: number; perGroupMinutes: number; maxPerDay: number };
  contextNotes: string;
  stats: {
    seen: number;
    candidates: number;
    gated: number;
    drafted: number;
    sent: number;
    sentToday: number;
    lastMessageAt?: string;
    lastReplyAt?: string;
  };
}

export interface UserbotContextLine {
  senderName: string;
  text: string;
  isOurs: boolean;
  messageId?: number;
}

export type UserbotDraftStage =
  | 'gate'
  /** سرویس هوش مصنوعی در دسترس نبود؛ پیام نگه داشته شده تا خودکار جواب بگیرد */
  | 'waiting'
  | 'skipped'
  | 'pending'
  | 'approved'
  | 'sending'
  | 'sent'
  | 'rejected'
  | 'expired'
  | 'failed';

export interface UserbotDraft {
  _id: string;
  groupId?: string;
  /** گفتگوی گروهی یا پیام خصوصی */
  kind?: 'group' | 'dm';
  chatId: string;
  chatTitle?: string;
  messageId: number;
  senderId: string;
  senderName: string;
  senderUsername?: string;
  text: string;
  context: UserbotContextLine[];
  isFollowUp: boolean;
  local?: UserbotLocalIntent;
  accountId?: { _id: string; label: string; username?: string } | null;
  stage: UserbotDraftStage;
  /** چرا منتظر مانده (وقتی stage === 'waiting') */
  waitReason?: string;
  aiAttempts?: number;
  gate?: { decision: 'reply' | 'ignore'; intent: string; confidence: number; reason: string; model: string; costUsd: number; ms: number };
  draft?: { text: string; model: string; costUsd: number; ms: number; note?: string; generatedAt: string };
  final?: { text: string; sentAt?: string; sentMessageId?: number; error?: string };
  decidedBy?: { _id: string; name?: string } | null;
  decidedAt?: string;
  expiresAt?: string;
  costUsd: number;
  retries?: number;
  sendAttempts?: number;
  products?: Array<{ id?: string; name: string; price?: number | null; inStock?: boolean }>;
  createdAt: string;
  updatedAt: string;
}

export interface UserbotSettings {
  enabled: boolean;
  apiId?: number;
  apiHashConfigured: boolean;
  provider: 'anthropic' | 'openai';
  aiViaBot?: boolean;
  openai: { baseUrl: string; apiKeyConfigured: boolean };
  gateModel: string;
  writerModel: string;
  dailyBudgetUsd: number;
  defaultMode: 'draft' | 'auto';
  prefilter: {
    minLength: number;
    maxLength: number;
    triggers: string[];
    roots: string[];
    negatives: string[];
    requireTrigger: boolean;
    ignoreForwarded: boolean;
    ignoreLinks: boolean;
    ignoreBots: boolean;
    minIntentScore: number;
  };
  gate: { thresholdLow: number; thresholdNormal: number; thresholdHigh: number; contextMessages: number };
  writer: { maxChars: number; contextMessages: number };
  persona: { globalRules: string; siteFacts: string; examples?: string };
  quietHours: { enabled: boolean; from: number; to: number };
  humanize: { minDelaySec: number; maxDelaySec: number; typingMsPerChar: number };
  draftExpiryMinutes: number;
  /** تا این‌قدر ساعت پیامِ بی‌جواب در صف انتظار می‌ماند (۰ = بی‌نهایت) */
  waitMaxHours: number;
  notifyAdminsOnDraft: boolean;
  coordination: { handoffMinutes: number; backupAllowed: boolean };
  failover?: {
    enabled: boolean;
    mode?: 'auto' | 'ask' | 'off';
    requireApproval: boolean;
    approvalTimeoutMinutes?: number;
    probeMinutes: number;
  };
  /** شناسهٔ چت تلگرام برای هشدار خرابی */
  alertChatIds?: string[];
  /** پیام خصوصی (دایرکت) — قواعدش جدا از گروه است */
  dm: {
    enabled: boolean;
    mode: 'draft' | 'auto';
    gate: 'always' | 'smart';
    gateThreshold: number;
    perUserMinutes: number;
    maxPerDay: number;
    ignoreForwarded: boolean;
    respectQuietHours: boolean;
    mayMentionSiteFirst: boolean;
    notes: string;
  };
}

export interface UserbotOverview {
  settings: UserbotSettings;
  ai: {
    provider?: 'anthropic' | 'openai';
    /** نام خواندنی سرویسی که واقعاً صدا زده می‌شود (مثلاً Google Gemini) */
    providerLabel?: string;
    baseUrl?: string;
    /** روی سرویس جایگزین هستیم، نه اصلی */
    usingOverride?: boolean;
    activeModels?: { gate: string; writer: string };
    apiKeyConfigured: boolean;
    baseUrlConfigured: boolean;
    breakerOpen: boolean;
    breakerOpenUntil: string | null;
    spentTodayUsd: number;
    dailyBudgetUsd: number;
    budgetExceeded: boolean;
    unavailableReason: string | null;
  };
  bridge: { ok: boolean; message: string; url?: string };
  problems: string[];
  counts: {
    accounts: number;
    accountsOnline: number;
    groups: number;
    groupsEnabled: number;
    pending: number;
    sentToday: number;
    skipped24h: number;
    repliesToday: number;
    dmEnabled?: boolean;
    dmSentToday?: number;
    /** پیام‌هایی که منتظر سالم‌شدن سرویس هوش مصنوعی‌اند */
    waiting?: number;
  };
  recent: UserbotDraft[];
}

export interface UserbotLocalIntent {
  intent: 'buy' | 'sell' | 'price' | 'safety' | 'howto' | 'none';
  confidence: number;
  scores: Record<string, number>;
  object?: string;
  /** برند */
  brand?: string;
  /** دغدغهٔ پوست/مو (جوش، لک، خشکی، ریزش مو، …) */
  concern?: string;
  /** نوع پوست/مو (چرب، خشک، حساس، …) */
  skinType?: string;
  budget?: string;
  isQuestion: boolean;
  isAd: boolean;
  offDomain?: boolean;
  signals: string[];
}

export interface UserbotLeadTurn {
  role: 'them' | 'us';
  text: string;
  at: string;
  messageId?: number;
  mentionedSite?: boolean;
  suggestedProducts?: string[];
}

export interface UserbotLeadProfile {
  object?: string;
  brand?: string;
  concern?: string;
  skinType?: string;
  budget?: string;
  intents?: string[];
  keywords?: string[];
}

export type UserbotLeadStage = 'new' | 'talking' | 'introduced' | 'interested' | 'cold';

export interface UserbotLead {
  _id: string;
  chatId: string;
  chatTitle?: string;
  groupId?: string;
  senderId: string;
  senderName: string;
  senderUsername?: string;
  accountId?: { _id: string; label: string; username?: string; online?: boolean } | null;
  stage: UserbotLeadStage;
  theirMessages: number;
  ourReplies: number;
  thread: UserbotLeadTurn[];
  turns?: number;
  profile?: UserbotLeadProfile;
  siteMentioned: boolean;
  siteMentionedAt?: string;
  productsSuggested?: Array<{ productId?: string; name: string; at: string }>;
  firstSeenAt?: string;
  lastMessageAt?: string;
  lastReplyAt?: string;
  note?: string;
  createdAt: string;
}

export interface UserbotLeadDetail {
  lead: UserbotLead;
  drafts: UserbotDraft[];
}

/** یک سرویس پشتیبانِ سازگار با OpenAI */
export interface UserbotFallback {
  id: string;
  label: string;
  baseUrl: string;
  gateModel: string;
  writerModel: string;
  enabled: boolean;
  apiKeyConfigured: boolean;
}

/** پیشنهاد یا اجرای جابه‌جایی مدل */
export interface UserbotModelOverride {
  status: 'proposed' | 'active';
  /** خودکار اعمال شد (ادمین فقط باید بداند) یا با تأیید */
  auto?: boolean;
  acknowledged?: boolean;
  providerId: string;
  providerLabel: string;
  gateModel: string;
  writerModel: string;
  reason: string;
  probe: string;
  proposedAt: string;
  approvedAt?: string;
}

/** یک کلید API روی یک سرویس */
/** عیب‌یابی صف ارسال — هر دروازه با دلیل دقیقش */
export interface UserbotQueueAccount {
  id: string;
  label: string;
  status: string;
  active: boolean;
  onlineDb: boolean;
  onlineLive: boolean | null;
  repliesToday: number;
  dailyCap: number;
  minGapSeconds: number;
  lastReplyAt?: string | null;
  floodWaitUntil?: string | null;
  lastError?: string | null;
  /** اگر پر باشد یعنی این حساب الان نمی‌تواند بفرستد */
  blockedBy: string | null;
}

export interface UserbotStuckDraft {
  id: string;
  kind: 'group' | 'dm';
  stage: string;
  chatTitle: string;
  senderName: string;
  ageMinutes: number;
  sendAttempts: number;
  lastError?: string | null;
  blockedBy: string;
}

export interface UserbotQueueDiagnosis {
  /** یک جمله: بزرگ‌ترین مانع چیست */
  verdict: string;
  enabled: boolean;
  bridge: { ok: boolean; message: string | null };
  liveKnown: boolean;
  liveConnected: string[] | null;
  counts: { approved: number; sending: number; pending: number; waiting: number; failed24: number };
  accounts: UserbotQueueAccount[];
  stuck: UserbotStuckDraft[];
  humanize: { minDelaySec: number; maxDelaySec: number; typingMsPerChar: number };
  checkedAt: string;
}

export interface UserbotAiKey {
  id: string;
  label: string;
  cooling: boolean;
  cooldownUntil?: string;
  lastError?: string;
  lastOkAt?: string;
  uses: number;
  /** کلید خودِ سرویس (از تنظیمات آن سرویس می‌آید، این‌جا حذف نمی‌شود) */
  builtin: boolean;
  active: boolean;
}

export interface UserbotProviderKeys {
  providerId: string;
  providerLabel: string;
  keys: UserbotAiKey[];
}

export interface UserbotAccountTest {
  providerId: string;
  providerLabel: string;
  accountId: string;
  label: string;
  ok: boolean;
  ms: number;
  kind?: string;
  message?: string;
  cooldownUntil?: string;
  /** با کدام مدل جواب داد (ممکن است مدل انتخابی نباشد) */
  model?: string;
  /** همهٔ مدل‌هایی که امتحان شدند */
  tried?: Array<{ model: string; ok: boolean; kind?: string }>;
}

export interface UserbotFailoverStatus {
  enabled: boolean;
  /** auto = جابه‌جا شو و خبر بده · ask = منتظر تأیید · off = هیچ‌وقت */
  mode: 'auto' | 'ask' | 'off';
  requireApproval: boolean;
  approvalTimeoutMinutes: number;
  probeMinutes: number;
  alertChatIds: string[];
  lastHealthyAt: string | null;
  active: { label: string; source: 'primary' | 'override'; baseUrl: string; gateModel: string; writerModel: string };
  override: UserbotModelOverride | null;
  fallbacks: UserbotFallback[];
  /** کلیدهای هر سرویس، به ترتیب چرخش */
  accounts: UserbotProviderKeys[];
  activeAccountId: string;
  waiting: number;
  /** 📚 دانش زندهٔ مدل‌ها: کدام مدل روی کدام سرویس واقعاً جواب داده */
  models?: UserbotModelKnowledge[];
  /** چند مدل آزموده و سالم روی سرویس فعلی داریم */
  modelsHealthy?: number;
}

/** یک ردیف از دفتر مدل‌ها — نتیجهٔ واقعیِ امتحان‌های قبلی */
export interface UserbotModelKnowledge {
  hostKey: string;
  providerLabel: string;
  model: string;
  ok: number;
  fail: number;
  failStreak: number;
  avgMs: number;
  lastOkAt: string | null;
  lastError: string | null;
  lastKind: 'quota' | 'rate' | 'auth' | 'retired' | 'other' | null;
  dead: boolean;
  deadUntil: string | null;
  proven: boolean;
}

/** یک ساعت از تایم‌لاین پیام‌ها (به وقت تهران) */
export interface UserbotTimelineRow {
  hour: string;
  received: number;
  sent: number;
  sentAt: number;
  failed: number;
  waiting: number;
  skipped: number;
  pending: number;
  other: number;
  topReason: string;
}

export interface UserbotProbe {
  ok: boolean;
  model: string;
  ms: number;
  message?: string;
}

export interface UserbotCandidateReport {
  providerId: string;
  label: string;
  baseUrl: string;
  isPrimary: boolean;
  tried: UserbotProbe[];
  working?: { gateModel: string; writerModel: string; ms: number };
}

export type ChannelCategory = 'news' | 'competitor' | 'market' | 'community' | 'other';

/** کانالی که دستیار فقط می‌خواندش — هیچ‌وقت در کانال پیام نمی‌دهد */
export interface UserbotChannel {
  _id: string;
  chatId: string;
  title: string;
  username?: string;
  membersCount?: number;
  enabled: boolean;
  category: ChannelCategory;
  note: string;
  accountIds: Array<{ _id: string; label: string; username?: string; online?: boolean; status?: string; active?: boolean }>;
  lastPostId: number;
  lastPostAt?: string;
  lastFetchAt?: string;
  stats: { posts: number; promos: number; priceMentions: number };
  createdAt: string;
  updatedAt: string;
}

export interface LeaveChannelsResult {
  total: number;
  done: number;
  left: number;
  failed: number;
  remaining: number;
  results: Array<{ id: string; title: string; ok: boolean; message?: string }>;
}

export interface UserbotChannelPost {
  _id: string;
  chatId: string;
  chatTitle?: string;
  messageId: number;
  text: string;
  at: string;
  views?: number;
  forwards?: number;
  hasMedia?: boolean;
  prices: number[];
  minPrice?: number;
  maxPrice?: number;
  object?: string;
  brand?: string;
  concern?: string;
  isPromo?: boolean;
  isNews?: boolean;
  isSale?: boolean;
}

export type InsightSource = 'groups' | 'channels';

export type InsightPriority = 'high' | 'medium' | 'low';
export type InsightActionStatus = 'todo' | 'doing' | 'done' | 'dropped';

export interface UserbotInsightAction {
  title: string;
  why?: string;
  category?: string;
  impact?: InsightPriority;
  effort?: InsightPriority;
  status: InsightActionStatus;
  note?: string;
  doneAt?: string;
}

export interface UserbotInsightStats {
  messages: number;
  groups: number;
  people: number;
  byIntent: Record<string, number>;
  topBrands: Array<{ name: string; n: number }>;
  topConcerns: Array<{ name: string; n: number }>;
  topSkinTypes: Array<{ name: string; n: number }>;
  topObjects: Array<{ name: string; n: number }>;
  questions: number;
  ads: number;
  /** فقط گزارش کانال‌ها */
  promos?: number;
  news?: number;
  priceMentions?: number;
  avgViews?: number;
  topChannels?: Array<{ name: string; n: number }>;
  priceBands?: Array<{ name: string; n: number }>;
}

export interface UserbotInsight {
  _id: string;
  day: string;
  source?: InsightSource;
  scope: 'daily' | 'manual' | 'weekly';
  status: 'ok' | 'empty' | 'failed';
  error?: string;
  periodFrom: string;
  periodTo: string;
  stats: UserbotInsightStats;
  summary?: string;
  actions: UserbotInsightAction[];
  demand: Array<{ item: string; signal?: string; volume?: string; note?: string }>;
  products: Array<{ name: string; why?: string; priority?: InsightPriority }>;
  problems: Array<{ problem: string; impact?: string; fix?: string }>;
  marketing: Array<{ idea: string; why?: string }>;
  quotes: Array<{ text: string; group?: string }>;
  news: Array<{ title: string; why?: string; source?: string }>;
  competitors: Array<{ name: string; what?: string; price?: string; note?: string }>;
  pricing: Array<{ item: string; price?: string; where?: string; note?: string }>;
  content: Array<{ idea: string; why?: string }>;
  aiModel?: string;
  costUsd: number;
  ms: number;
  createdBy: string;
  createdAt: string;
}

export interface UserbotInsightRow {
  _id: string;
  day: string;
  source?: InsightSource;
  scope: string;
  status: 'ok' | 'empty' | 'failed';
  stats?: { messages?: number };
  summary?: string;
  actionsTotal: number;
  actionsDone: number;
  costUsd?: number;
  createdAt: string;
}

export interface UserbotOpenAction extends UserbotInsightAction {
  insightId: string;
  day: string;
  index: number;
}

export interface UserbotDryRun {
  prefilter: { pass: boolean; reason: string; matched: string[] };
  local?: UserbotLocalIntent;
  error?: string;
  gate?: UserbotDraft['gate'];
  threshold?: number;
  writer?: { text: string; skip: boolean; note?: string; model: string; costUsd: number; ms: number };
  persona?: { label?: string; displayName?: string };
}

const unwrap = (r: any) => (r && typeof r === 'object' && 'data' in r ? r.data : r);
const message = (r: any) => (r && typeof r === 'object' && r.message ? String(r.message) : '');

class UserbotService {
  private base = '/admin/userbot';

  async overview(): Promise<UserbotOverview> {
    return unwrap(await api.get(`${this.base}/overview`));
  }
  async listModels(): Promise<{ ok: boolean; models: string[]; message?: string }> {
    return unwrap(await api.get(`${this.base}/models`));
  }
  async liveStatus(): Promise<any> {
    return unwrap(await api.get(`${this.base}/live-status`));
  }
  async resetMessages(): Promise<string> {
    return message(await api.post(`${this.base}/reset-messages`, {}));
  }
  async reload(): Promise<string> {
    return message(await api.post(`${this.base}/reload`, {}));
  }

  async getSettings(): Promise<UserbotSettings> {
    return unwrap(await api.get(`${this.base}/settings`));
  }
  async updateSettings(patch: Partial<Omit<UserbotSettings, 'openai'>> & { apiHash?: string; openai?: { baseUrl?: string; apiKey?: string } }): Promise<UserbotSettings> {
    return unwrap(await api.put(`${this.base}/settings`, patch));
  }

  async accounts(): Promise<UserbotAccount[]> {
    return unwrap(await api.get(`${this.base}/accounts`));
  }
  async createAccount(body: { label: string; phone: string; persona?: Partial<UserbotPersona> }): Promise<UserbotAccount> {
    return unwrap(await api.post(`${this.base}/accounts`, body));
  }
  async updateAccount(id: string, body: Record<string, any>): Promise<UserbotAccount> {
    return unwrap(await api.put(`${this.base}/accounts/${id}`, body));
  }
  async deleteAccount(id: string): Promise<void> {
    await api.delete(`${this.base}/accounts/${id}`);
  }
  async loginStart(id: string): Promise<{ status: string; isCodeViaApp: boolean; message: string }> {
    const r: any = await api.post(`${this.base}/accounts/${id}/login/start`, {});
    return { ...unwrap(r), message: message(r) };
  }
  async loginCode(id: string, code: string): Promise<{ status: string; passwordNeeded?: boolean; message: string }> {
    const r: any = await api.post(`${this.base}/accounts/${id}/login/code`, { code });
    return { ...unwrap(r), message: message(r) };
  }
  async loginPassword(id: string, password: string): Promise<{ status: string; message: string }> {
    const r: any = await api.post(`${this.base}/accounts/${id}/login/password`, { password });
    return { ...unwrap(r), message: message(r) };
  }
  async logout(id: string): Promise<string> {
    return message(await api.post(`${this.base}/accounts/${id}/logout`, {}));
  }
  async refreshDialogs(id: string): Promise<string> {
    return message(await api.post(`${this.base}/accounts/${id}/dialogs`, {}));
  }
  async join(id: string, link: string): Promise<string> {
    return message(await api.post(`${this.base}/accounts/${id}/join`, { link }));
  }
  async leave(id: string, chatId: string): Promise<string> {
    return message(await api.post(`${this.base}/accounts/${id}/leave`, { chatId }));
  }

  async groups(): Promise<UserbotGroup[]> {
    return unwrap(await api.get(`${this.base}/groups`));
  }
  async updateGroup(id: string, body: Record<string, any>): Promise<UserbotGroup> {
    return unwrap(await api.put(`${this.base}/groups/${id}`, body));
  }
  /** leave=true: همهٔ حساب‌ها از گروه خارج می‌شوند و بعد حذف می‌شود */
  async deleteGroup(id: string, leave = false): Promise<string> {
    return message(await api.delete(`${this.base}/groups/${id}${leave ? '?leave=1' : ''}`));
  }

  // ── صف ارسال ──
  /** چرا هیچ‌چیز ارسال نمی‌شود — همهٔ دروازه‌ها با دلیل دقیق */
  async diagnoseQueue(): Promise<UserbotQueueDiagnosis> {
    return unwrap(await api.get(`${this.base}/queue/diagnose`));
  }
  /** همین حالا صف را خالی کن */
  async flushQueue(): Promise<{ data: UserbotQueueDiagnosis; message: string }> {
    const r: any = await api.post(`${this.base}/queue/flush`, {});
    return { data: unwrap(r), message: message(r) };
  }
  /** پیام‌های رهاشده را به صف برگردان */
  async retryFailedDrafts(ids?: string[]): Promise<{ data: UserbotQueueDiagnosis; message: string }> {
    const r: any = await api.post(`${this.base}/queue/retry-failed`, ids?.length ? { ids } : {});
    return { data: unwrap(r), message: message(r) };
  }

  // ── جابه‌جایی سرویس/مدل ──
  async failover(): Promise<UserbotFailoverStatus> {
    return unwrap(await api.get(`${this.base}/failover`));
  }
  /** سنجش همهٔ سرویس‌ها و مدل‌ها، بدون تغییر چیزی */
  async failoverScan(): Promise<{ result: { reports: UserbotCandidateReport[]; winner: UserbotCandidateReport | null }; message: string }> {
    const r: any = await api.post(`${this.base}/failover/scan`, {});
    return { result: unwrap(r), message: message(r) };
  }
  /** یک دور کامل: برگشت به اصلی یا ثبت پیشنهاد */
  async failoverCheck(): Promise<{ status: UserbotFailoverStatus; message: string }> {
    const r: any = await api.post(`${this.base}/failover/check`, {});
    return { status: unwrap(r), message: message(r) };
  }
  /** «دیدم» — بنر جابه‌جایی خودکار را ببند */
  async failoverAck(): Promise<{ status: UserbotFailoverStatus; message: string }> {
    const r: any = await api.post(`${this.base}/failover/ack`, {});
    return { status: unwrap(r), message: message(r) };
  }
  /** آزمایش مسیر هشدار — قبل از خرابی واقعی */
  async testAlert(): Promise<{ result: { chatIds: number; message: string }; message: string }> {
    const r: any = await api.post(`${this.base}/failover/test-alert`, {});
    return { result: unwrap(r), message: message(r) };
  }
  async failoverApprove(): Promise<{ status: UserbotFailoverStatus; message: string }> {
    const r: any = await api.post(`${this.base}/failover/approve`, {});
    return { status: unwrap(r), message: message(r) };
  }
  async failoverReject(): Promise<{ status: UserbotFailoverStatus; message: string }> {
    const r: any = await api.post(`${this.base}/failover/reject`, {});
    return { status: unwrap(r), message: message(r) };
  }
  /** افزودن/ویرایش یک کلید API. چند کلید روی یک سرویس مجاز است. */
  async saveAiAccount(body: {
    id?: string;
    providerId: string;
    label: string;
    apiKey?: string;
    enabled?: boolean;
    clearCooldown?: boolean;
  }): Promise<{ status: UserbotFailoverStatus; message: string }> {
    const r: any = await api.put(`${this.base}/failover/accounts`, body);
    return { status: unwrap(r), message: message(r) };
  }
  async deleteAiAccount(id: string): Promise<{ status: UserbotFailoverStatus; message: string }> {
    const r: any = await api.delete(`${this.base}/failover/accounts/${id}`);
    return { status: unwrap(r), message: message(r) };
  }
  /** هر کلید را جداگانه امتحان کن — کدام هنوز سهمیه دارد */
  async testAiAccounts(providerId?: string): Promise<UserbotAccountTest[]> {
    return unwrap(await api.post(`${this.base}/failover/accounts/test`, providerId === undefined ? {} : { providerId }));
  }

  async saveFallback(body: {
    id?: string;
    label: string;
    baseUrl: string;
    apiKey?: string;
    gateModel?: string;
    writerModel?: string;
    enabled?: boolean;
  }): Promise<{ status: UserbotFailoverStatus; message: string }> {
    const r: any = await api.put(`${this.base}/failover/fallbacks`, body);
    return { status: unwrap(r), message: message(r) };
  }
  async deleteFallback(id: string): Promise<{ status: UserbotFailoverStatus; message: string }> {
    const r: any = await api.delete(`${this.base}/failover/fallbacks/${id}`);
    return { status: unwrap(r), message: message(r) };
  }

  async channels(): Promise<UserbotChannel[]> {
    return unwrap(await api.get(`${this.base}/channels`));
  }
  async updateChannel(
    id: string,
    body: { enabled?: boolean; category?: ChannelCategory; note?: string; title?: string }
  ): Promise<UserbotChannel> {
    return unwrap(await api.put(`${this.base}/channels/${id}`, body));
  }
  /** leave=true: حساب‌ها هم از کانال بیرون می‌آیند */
  async deleteChannel(id: string, leave = false): Promise<string> {
    return message(await api.delete(`${this.base}/channels/${id}${leave ? '?leave=1' : ''}`));
  }
  /**
   * خروج از کانال‌ها — بدون `ids` یعنی همهٔ کانال‌ها.
   * سرور هر بار حداکثر چند کانال را انجام می‌دهد و `remaining` را برمی‌گرداند،
   * پس تا وقتی صفر نشده باید دوباره صدا زده شود (پنل همین کار را می‌کند).
   */
  async leaveChannels(ids?: string[], keep = false): Promise<{ result: LeaveChannelsResult; message: string }> {
    const r: any = await api.post(`${this.base}/channels/leave`, { ...(ids?.length ? { ids } : {}), keep });
    return { result: unwrap(r), message: message(r) };
  }
  async channelPosts(id: string): Promise<UserbotChannelPost[]> {
    return unwrap(await api.get(`${this.base}/channels/${id}/posts`));
  }
  /** یک حساب را عضو کانال کن (لینک یا یوزرنیم) */
  async joinChannel(accountId: string, link: string): Promise<string> {
    return message(await api.post(`${this.base}/accounts/${accountId}/join-channel`, { link }));
  }

  async leads(params: { stage?: string; q?: string; groupId?: string; page?: number; limit?: number }): Promise<{
    items: UserbotLead[];
    total: number;
    page: number;
    totalPages: number;
    byStage: Record<string, number>;
  }> {
    const p = new URLSearchParams();
    if (params.stage) p.set('stage', params.stage);
    if (params.q) p.set('q', params.q);
    if (params.groupId) p.set('groupId', params.groupId);
    if (params.page) p.set('page', String(params.page));
    if (params.limit) p.set('limit', String(params.limit));
    return unwrap(await api.get(`${this.base}/leads?${p.toString()}`));
  }
  async lead(id: string): Promise<UserbotLeadDetail> {
    return unwrap(await api.get(`${this.base}/leads/${id}`));
  }
  async updateLead(id: string, body: { note?: string; stage?: string }): Promise<UserbotLead> {
    return unwrap(await api.put(`${this.base}/leads/${id}`, body));
  }

  async insights(
    page = 1,
    limit = 30,
    source: InsightSource = 'groups'
  ): Promise<{ items: UserbotInsightRow[]; total: number; page: number; totalPages: number }> {
    return unwrap(await api.get(`${this.base}/insights?page=${page}&limit=${limit}&source=${source}`));
  }
  async insight(id: string): Promise<UserbotInsight> {
    return unwrap(await api.get(`${this.base}/insights/${id}`));
  }
  async runInsight(day?: string, source: InsightSource = 'groups'): Promise<{ insight: UserbotInsight; message: string }> {
    const r: any = await api.post(`${this.base}/insights/run`, { source, ...(day ? { day } : {}) });
    return { insight: unwrap(r), message: message(r) };
  }
  async setInsightAction(id: string, index: number, status: InsightActionStatus, note?: string): Promise<UserbotInsight> {
    return unwrap(await api.patch(`${this.base}/insights/${id}/actions/${index}`, { status, ...(note ? { note } : {}) }));
  }
  async openInsightActions(): Promise<UserbotOpenAction[]> {
    return unwrap(await api.get(`${this.base}/insights/actions/open`));
  }

  async drafts(params: { stage?: string; page?: number; limit?: number; groupId?: string }): Promise<{
    items: UserbotDraft[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }> {
    const q = new URLSearchParams();
    if (params.stage) q.set('stage', params.stage);
    if (params.page) q.set('page', String(params.page));
    if (params.limit) q.set('limit', String(params.limit));
    if (params.groupId) q.set('groupId', params.groupId);
    return unwrap(await api.get(`${this.base}/drafts?${q.toString()}`));
  }
  /** تایم‌لاین ساعت‌به‌ساعت: کِی کار می‌کرده، کِی نه، و چرا */
  async draftsTimeline(hours = 48): Promise<{ hours: number; rows: UserbotTimelineRow[] }> {
    return unwrap(await api.get(`${this.base}/drafts/timeline?hours=${hours}`));
  }
  async approve(id: string, text?: string): Promise<string> {
    return message(await api.post(`${this.base}/drafts/${id}/approve`, text !== undefined ? { text } : {}));
  }
  async reject(id: string): Promise<string> {
    return message(await api.post(`${this.base}/drafts/${id}/reject`, {}));
  }
  async regenerate(id: string, hint?: string): Promise<{ draft: UserbotDraft; message: string }> {
    const r: any = await api.post(`${this.base}/drafts/${id}/regenerate`, hint ? { hint } : {});
    return { draft: unwrap(r), message: message(r) };
  }

  async dryRun(body: { text: string; groupId?: string; accountId?: string; context?: UserbotContextLine[] }): Promise<UserbotDryRun> {
    return unwrap(await api.post(`${this.base}/test`, body));
  }
}

const userbotService = new UserbotService();
export default userbotService;
